import { useEffect, useState, useRef } from 'react';
import { Button, CircularProgress, FormControl, TextField, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { Close as CloseIcon, RadioButtonChecked, RadioButtonUnchecked, CheckCircleOutlined } from '@mui/icons-material';
// Our components
import { Analytics } from 'lib/api';
import CircularComponent from 'components/shared/CircularComponent';
import { KYB } from 'lib/api';
import { KYBStepBanner } from './KYBStepBanner';
import { KYX_ANALYTICS_EVENTS } from '../StartKYX';
import { Sophtron } from '../../OverviewSection/Accounts/banklinking/Sophtron';
import StatusSnackbar from 'components/shared/StatusSnackbar';
// Utils and Helpers
import { BUSINESS_FUNDING_TYPE } from 'helpers/constants';

const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    phoneBox: {
        marginTop: '2em'
    },
    phoneInput: {
        width: '50%'
    },
    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important',
        background: '#1BABE2!important'
    },
    inputFormControl: {
        background: '#FFFFFF',
        border: '1px solid #D8DDE2',
        boxSizing: 'border-box',
        borderRadius: '3px',
        marginTop: '10px',
        width: '100%!important',
        height: '40px'
    },
    contactInforFormLabel: {
        color: '#225384',
        fontWeight: 600
    },
    businessAdressBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    formControl: {
        width: '100%!important',
        rowGap: '.3em'
    },
    informationOptions: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    },
    businessOwnerFields: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    achContent: {
        width: '100%'
    },
    fundingType: {
        display: 'flex',
        border: '1px solid #d0d5d9',
        cursor: 'pointer',
        marginBottom: '12px',
        padding: '16px 20px',
        borderRadius: '4px'
    },
    fundingTypeName: {
        fontWeight: 500,
        fontSize: '13px',
        lineHeight: '20px'
    },
    fundingTypeDescription: {
        color: '#617380',
        lineHeight: '18px',
        fontWeight: 500
    },
    fundingTypeChecked: {
        backgroundColor: 'rgba(27, 171, 226, 0.05)',
        border: '1px solid var(--info-color)'
    }
});

export const KYBSeventhStep = ({ incrementPage, activePage, decreasePage, incrementStep, handleClose }) => {
    const classes = useStyles();
    const dialogBody = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [manualBankLogin, setManualBankLogin] = useState(false);
    const [confirmAcc, setConfirmAcc] = useState(true);
    const [openAlert, setOpenAlert] = useState(false);
    const [needsAction, setNeedsAction] = useState(false);
    const [label, setLabel] = useState('');
    const [isSophtronOpen, setIsSophtronOpen] = useState(false);
    const [fundingType, setFundingType] = useState(BUSINESS_FUNDING_TYPE.CREDIT_LINE);
    const [formValues, setFormValues] = useState({
        bankName: '',
        routingNumber: '',
        bankAccount: '',
        confirmBankAccount: ''
    });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        setIsSophtronOpen(true);
    }, []);

    const handleKYBComplete = () => {
        window.dataLayer.push({
            event: 'virtual-card-bank-account-added'
        });
        const body = {
            step: KYX_ANALYTICS_EVENTS.STEP_8,
            eventData: {}
        };
        Analytics.createKYXEvent(body).catch(error => console.error(error));
        incrementPage();
    };

    const handleInputChange = ({ target: { name, value } }) => {
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    const validateForm = () => {
        const errors = {};

        Object.keys(formValues).forEach(key => {
            if (!formValues[key]) {
                errors[key] = 'This field is required';
            }
        });

        if (formValues.bankAccount !== formValues.confirmBankAccount) {
            errors['confirmBankAccount'] = 'Bank account does not match';
            errors['bankAccount'] = 'Bank account does not match';
        }

        setFormErrors(errors);

        // If there are any errors, the form is not valid
        return Object.keys(errors).length === 0;
    };

    const handleSetFundingType = () => {
        if (validateForm()) {
            setIsLoading(true);
            const bankPrefundAccountData = {
                bankName: formValues.bankName,
                accountNumber: formValues.bankAccount,
                routingNumber: formValues.routingNumber,
                accountType: 'prefundingAccount'
            };

            KYB.postBankPrefundAccount(bankPrefundAccountData)
                .then(() => {
                    handleKYBComplete();
                })
                .catch(error => {
                    console.error(error);
                });

            if (fundingType === BUSINESS_FUNDING_TYPE.PREFUNDING) {
                KYB.updateFundingType({ funding_type: fundingType })
                    .then(() => {
                        handleKYBComplete();
                    })
                    .catch(error => {
                        console.error(error);
                    });
            }
            setIsLoading(false);
        }
    };

    return (
        <Box className={classes.dialogBody} ref={dialogBody}>
            <KYBStepBanner activeStep={activePage} incrementStep={() => incrementStep()} />
            <Box className={classes.content} id="kyb-bankHeader">
                <Typography variant="h5" className={classes.contentTitle}>
                    Trouble linking your bank account?
                </Typography>
                <Typography variant="subtitle1" style={{ color: '#A8B6C1' }}>
                    Try again, or opt-in to pre-fund your account directly
                </Typography>
                <Box
                    display="flex"
                    className={`${classes.fundingType} ${
                        fundingType === BUSINESS_FUNDING_TYPE.CREDIT_LINE ? classes.fundingTypeChecked : ''
                    }`}
                    sx={{ marginTop: '30px' }}
                    onClick={() => setFundingType(BUSINESS_FUNDING_TYPE.CREDIT_LINE)}>
                    {fundingType === BUSINESS_FUNDING_TYPE.CREDIT_LINE ? (
                        <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                    ) : (
                        <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                    )}
                    <Box>
                        <Typography component="div" className={classes.fundingTypeName} sx={{ marginBottom: '12px' }}>
                            Link Bank Account & Qualify for a Pre-Funded Spend Limit
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => setIsSophtronOpen(!isSophtronOpen)}
                            disabled={isLoading || manualBankLogin}
                            sx={{ marginBottom: '12px' }}>
                            {isLoading ? (
                                <CircularProgress />
                            ) : manualBankLogin ? (
                                'Automatic bank linking failed.'
                            ) : (
                                'Link Bank Account'
                            )}
                        </Button>
                        <Typography component="div" className={classes.fundingTypeDescription}>
                            <Box display="flex" alignItems="center" sx={{ marginBottom: '6px' }}>
                                <CheckCircleOutlined fontSize="15px" sx={{ marginRight: '5px' }} />
                                <Box component="span" fontSize="12px">
                                    Paystand advances your business a pre-funded account spend limit
                                </Box>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <CheckCircleOutlined fontSize="15px" sx={{ marginRight: '5px' }} />
                                <Box component="span" fontSize="12px">
                                    Paystand initiates ACH pulls on the bank account you link to recuperate the advanced funds on
                                    a daily basis
                                </Box>
                            </Box>
                        </Typography>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    className={`${classes.fundingType} ${
                        fundingType === BUSINESS_FUNDING_TYPE.PREFUNDING ? classes.fundingTypeChecked : ''
                    }`}
                    onClick={() => setFundingType(BUSINESS_FUNDING_TYPE.PREFUNDING)}>
                    {fundingType === BUSINESS_FUNDING_TYPE.PREFUNDING ? (
                        <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                    ) : (
                        <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                    )}
                    <Box>
                        <Box sx={{ marginBottom: '16px' }}>
                            <Typography component="div" className={classes.fundingTypeName}>
                                Prefund your Paystand Account with Wire Transfer
                            </Typography>
                            <Typography component="div" className={classes.fundingTypeDescription} fontSize="12px">
                                Add a bank Account you would like to wire funds from. You will receive an email from Paystand with
                                the wire instructions upon account approval.
                            </Typography>
                        </Box>
                        <Box
                            display={fundingType === BUSINESS_FUNDING_TYPE.CREDIT_LINE ? 'none' : 'block'}
                            sx={{ marginRight: '32px' }}>
                            <FormControl fullWidth sx={{ marginBottom: '16px' }} required>
                                <Typography variant="subtitle2">Bank Name</Typography>
                                <TextField
                                    name="bankName"
                                    placeholder="Enter bank name"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    value={formValues.bankName}
                                    size="small"
                                    error={!!formErrors.bankName}
                                    helperText={formErrors.bankName}
                                />
                            </FormControl>
                            <FormControl fullWidth sx={{ marginBottom: '16px' }} required>
                                <Typography variant="subtitle2">Routing Number</Typography>
                                <TextField
                                    name="routingNumber"
                                    placeholder="Enter routing number"
                                    variant="outlined"
                                    onChange={handleInputChange}
                                    value={formValues.routingNumber}
                                    size="small"
                                    type="number"
                                    error={!!formErrors.routingNumber}
                                    helperText={formErrors.routingNumber}
                                />
                            </FormControl>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <FormControl fullWidth sx={{ marginRight: '16px' }} required>
                                    <Typography variant="subtitle2">Bank Account</Typography>
                                    <TextField
                                        name="bankAccount"
                                        placeholder="Enter bank account"
                                        variant="outlined"
                                        onChange={handleInputChange}
                                        value={formValues.bankAccount}
                                        size="small"
                                        error={!!formErrors.bankAccount}
                                        helperText={formErrors.bankAccount}
                                    />
                                </FormControl>
                                <FormControl fullWidth required>
                                    <Typography variant="subtitle2">Confirm Bank Account</Typography>
                                    <TextField
                                        name="confirmBankAccount"
                                        placeholder="Enter bank account"
                                        variant="outlined"
                                        onChange={handleInputChange}
                                        value={formValues.confirmBankAccount}
                                        size="small"
                                        error={!!formErrors.confirmBankAccount}
                                        helperText={formErrors.confirmBankAccount}
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {manualBankLogin ? <Box className={classes.achContent}></Box> : null}
                <Box className={classes.informationOptions}>
                    {isLoading ? (
                        <CircularComponent />
                    ) : (
                        <>
                            <Button variant="text" disabled={isLoading} onClick={decreasePage}>
                                Back
                            </Button>
                            <Button variant="contained" disabled={!confirmAcc} onClick={handleSetFundingType}>
                                Next
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
            <IconButton
                edge="start"
                color="inherit"
                onClick={handleClose}
                aria-label="close"
                sx={{ alignSelf: 'flex-start', margin: '25px' }}>
                <CloseIcon />
            </IconButton>
            <StatusSnackbar
                label={label}
                severity={'error'}
                open={openAlert}
                setOpenAlert={setOpenAlert}
                needsAction={needsAction}
                decreasePage={decreasePage}
            />
            {isSophtronOpen && <Sophtron shown={isSophtronOpen} setShown={setIsSophtronOpen} onComplete={handleKYBComplete} />}
        </Box>
    );
};
